// پوسته اپ (IA §2.1): Header ثابت، Sidebar جمع‌شونده مبتنی بر دسترسی، نوار حالت دسترسی، Breadcrumb و محتوا.
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@phoenix/i18n';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Link as RouterLink, useLocation } from 'react-router';
import { UpgradeBadge } from '../components/display';
import { layout } from '../theme/tokens';
import { ColorModeToggle } from './ColorModeToggle';
import type { ResolvedMenuItem } from './menu';
import { ShortcutHelp, type ShellShortcut } from './ShortcutHelp';

export interface Crumb {
  labelKey: string;
  path?: string;
}

export interface AppShellProps {
  /** کلید i18n نام اپ (عنوان Header). */
  appNameKey: string;
  /** لوگو یا نام مرکز در ابتدای Header. */
  brand?: ReactNode;
  menu: readonly ResolvedMenuItem[];
  /** صفحه اشتراک برای آیتم‌های نیازمند ارتقا. */
  upgradePath?: string;
  /** محتوای وسط Header (انتخاب شعبه، جستجو). */
  headerContent?: ReactNode;
  /** اقدام‌های انتهای Header (اعلان، حساب). */
  headerActions?: ReactNode;
  /** میانبرهای همین اپ، علاوه بر راهنمای `?`. */
  shortcuts?: readonly ShellShortcut[];
  /** نوار حالت دسترسی و هشدارهای سراسری. */
  banner?: ReactNode;
  breadcrumbs?: readonly Crumb[];
  children: ReactNode;
}

function isActive(pathname: string, path: string): boolean {
  return path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
}

function MenuEntry({
  item,
  collapsed,
  upgradePath,
  depth,
}: {
  item: ResolvedMenuItem;
  collapsed: boolean;
  upgradePath?: string;
  depth: number;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const Icon = item.icon;
  const label = t(item.labelKey);
  const target = item.requiresUpgrade && upgradePath ? upgradePath : item.path;

  const button = (
    <ListItemButton
      component={NavLink}
      to={target}
      selected={!item.requiresUpgrade && isActive(pathname, item.path)}
      data-menu-id={item.id}
      data-requires-upgrade={item.requiresUpgrade ? 'true' : undefined}
      sx={{ ps: collapsed ? 2.5 : 2 + depth * 2, minHeight: 40 }}
      aria-label={collapsed ? label : undefined}
    >
      {Icon && (
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Icon />
        </ListItemIcon>
      )}
      {!collapsed && <ListItemText primary={label} />}
      {!collapsed && item.requiresUpgrade && <UpgradeBadge feature={item.feature} />}
    </ListItemButton>
  );

  return collapsed ? (
    <Tooltip title={label} placement="left">
      {button}
    </Tooltip>
  ) : (
    button
  );
}

function GroupHeading({
  item,
  collapsed,
  open,
  onToggle,
}: {
  item: ResolvedMenuItem;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const label = t(item.labelKey);
  const Icon = item.icon;
  return (
    <ListItemButton aria-expanded={open} aria-label={collapsed ? label : undefined} onClick={onToggle}>
      {Icon ? (
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Icon />
        </ListItemIcon>
      ) : null}
      {collapsed ? null : <ListItemText primary={label} />}
      {collapsed ? null : open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
    </ListItemButton>
  );
}

function MenuList({
  items,
  collapsed,
  upgradePath,
}: {
  items: readonly ResolvedMenuItem[];
  collapsed: boolean;
  upgradePath?: string;
}) {
  const { pathname } = useLocation();
  // گروهی که صفحه جاری در آن است باز شروع می‌شود تا قلم فعال دیده شود؛ بقیه بسته‌اند (C-AGENT-1013).
  const activeGroupId =
    items.find((item) => item.children?.some((child) => !child.requiresUpgrade && isActive(pathname, child.path)))?.id ?? null;
  const [openGroupId, setOpenGroupId] = useState<string | null>(activeGroupId);
  useEffect(() => {
    if (activeGroupId) {
      setOpenGroupId(activeGroupId);
    }
  }, [activeGroupId]);
  return (
    <>
      {items.map((item) =>
        item.children ? (
          <Box key={item.id} data-menu-group={item.id} data-menu-items={item.children.map((child) => child.id).join(' ')}>
            <GroupHeading item={item} collapsed={collapsed} open={openGroupId === item.id} onToggle={() => setOpenGroupId((current) => (current === item.id ? null : item.id))} />
            {/* در Sidebar جمع‌شده هم زیرقلم‌های گروه باز به‌صورت آیکن نمایش داده می‌شوند تا صفحه‌ها در دسترس بمانند. */}
            {openGroupId === item.id ? (
              <List component="div" disablePadding>
                {item.children.map((child) => (
                  <MenuEntry
                    key={child.id}
                    item={child}
                    collapsed={collapsed}
                    upgradePath={upgradePath}
                    depth={1}
                  />
                ))}
              </List>
            ) : null}
          </Box>
        ) : (
          <MenuEntry key={item.id} item={item} collapsed={collapsed} upgradePath={upgradePath} depth={0} />
        ),
      )}
    </>
  );
}

export function AppShell({
  appNameKey,
  brand,
  menu,
  upgradePath,
  headerContent,
  headerActions,
  shortcuts,
  banner,
  breadcrumbs,
  children,
}: AppShellProps) {
  const { t } = useTranslation('common');
  const [collapsed, setCollapsed] = useState(false);
  const width = collapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }} data-testid="app-shell">
      <Link
        href="#main-content"
        sx={{
          position: 'absolute',
          insetInlineStart: -9999,
          '&:focus': { insetInlineStart: 8, top: 8, zIndex: 2000, bgcolor: 'background.paper', p: 1 },
        }}
      >
        {t('common:shell.skip_to_content')}
      </Link>
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar variant="dense" sx={{ minHeight: layout.headerHeight, gap: 1.5 }}>
          <IconButton
            edge="start"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={t(collapsed ? 'common:shell.expand_sidebar' : 'common:shell.collapse_sidebar')}
            aria-expanded={!collapsed}
            aria-controls="phx-sidebar"
          >
            {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
          </IconButton>
          {brand}
          <Typography variant="h2" component="span" sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>
            {t(appNameKey)}
          </Typography>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {headerContent}
          </Box>
          {headerActions}
          <ShortcutHelp shortcuts={shortcuts} />
          <ColorModeToggle />
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        id="phx-sidebar"
        sx={{
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: 'width 150ms',
            bgcolor: 'background.sidebar',
          },
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: layout.headerHeight }} />
        <Box component="nav" aria-label={t('common:shell.main_navigation')} data-testid="sidebar">
          <List component="div">
            <MenuList items={menu} collapsed={collapsed} upgradePath={upgradePath} />
          </List>
        </Box>
      </Drawer>
      <Box component="main" id="main-content" tabIndex={-1} sx={{ flex: 1, minWidth: 0, outline: 'none' }}>
        <Toolbar variant="dense" sx={{ minHeight: layout.headerHeight }} />
        {banner}
        <Box sx={{ p: 2 }}>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumbs aria-label={t('common:shell.breadcrumb')} sx={{ mb: 1 }} data-testid="breadcrumbs">
              {breadcrumbs.map((crumb, index) =>
                crumb.path && index < breadcrumbs.length - 1 ? (
                  <Link
                    key={crumb.labelKey}
                    component={RouterLink}
                    to={crumb.path}
                    underline="hover"
                    color="inherit"
                  >
                    {t(crumb.labelKey)}
                  </Link>
                ) : (
                  <Typography key={crumb.labelKey} color="text.primary" aria-current="page">
                    {t(crumb.labelKey)}
                  </Typography>
                ),
              )}
            </Breadcrumbs>
          )}
          {children}
        </Box>
      </Box>
    </Box>
  );
}
